// ── V122: 한/영 안 바꾸고 친 글자 알아듣기 ──────────────────────────────────
// 윈도우 크롬은 입력칸을 새로 누르면 영어 상태로 시작하는 경우가 많다(웹앱이 한/영을 바꿀 수는 없음).
// 그래서 "rkswkd"처럼 영어 상태로 친 걸 두벌식 자판 그대로 한글("간장")로 바꿔서 검색에 같이 쓴다.

const KEY = {
  r: 'ㄱ', R: 'ㄲ', s: 'ㄴ', e: 'ㄷ', E: 'ㄸ', f: 'ㄹ', a: 'ㅁ', q: 'ㅂ', Q: 'ㅃ', t: 'ㅅ', T: 'ㅆ',
  d: 'ㅇ', w: 'ㅈ', W: 'ㅉ', c: 'ㅊ', z: 'ㅋ', x: 'ㅌ', v: 'ㅍ', g: 'ㅎ',
  k: 'ㅏ', o: 'ㅐ', O: 'ㅒ', i: 'ㅑ', j: 'ㅓ', p: 'ㅔ', P: 'ㅖ', u: 'ㅕ', h: 'ㅗ', y: 'ㅛ',
  n: 'ㅜ', b: 'ㅠ', m: 'ㅡ', l: 'ㅣ',
};
const CHO  = 'ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ';
const JUNG = 'ㅏㅐㅑㅒㅓㅔㅕㅖㅗㅘㅙㅚㅛㅜㅝㅞㅟㅠㅡㅢㅣ';
const JONG = ['', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ',
              'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
const VOWEL_PAIR = { 'ㅗㅏ': 'ㅘ', 'ㅗㅐ': 'ㅙ', 'ㅗㅣ': 'ㅚ', 'ㅜㅓ': 'ㅝ', 'ㅜㅔ': 'ㅞ', 'ㅜㅣ': 'ㅟ', 'ㅡㅣ': 'ㅢ' };
const JONG_PAIR  = { 'ㄱㅅ': 'ㄳ', 'ㄴㅈ': 'ㄵ', 'ㄴㅎ': 'ㄶ', 'ㄹㄱ': 'ㄺ', 'ㄹㅁ': 'ㄻ', 'ㄹㅂ': 'ㄼ', 'ㄹㅅ': 'ㄽ',
                     'ㄹㅌ': 'ㄾ', 'ㄹㅍ': 'ㄿ', 'ㄹㅎ': 'ㅀ', 'ㅂㅅ': 'ㅄ' };
const JONG_SPLIT = Object.fromEntries(Object.entries(JONG_PAIR).map(([ab, j]) => [j, [ab[0], ab[1]]]));

const isVowel = (j) => JUNG.includes(j);

function keyToJamo(ch) {
  if (KEY[ch]) return KEY[ch];
  const lower = ch.toLowerCase();
  return /[A-Z]/.test(ch) && KEY[lower] ? KEY[lower] : null;   // Shift가 의미 없는 키(K→ㅏ)
}

/** 영어 상태로 친 글자를 두벌식 한글로. 영문자가 아닌 글자(숫자·공백·한글)는 그대로 둔다. */
export function engToKor(input) {
  const s = String(input == null ? '' : input);
  let out = '';
  let cho = '', jung = '', jong = '';
  const flush = () => {
    if (cho && jung) {
      out += String.fromCharCode(0xAC00 + (CHO.indexOf(cho) * 21 + JUNG.indexOf(jung)) * 28 + JONG.indexOf(jong));
    } else {
      out += cho + jung + jong;
    }
    cho = ''; jung = ''; jong = '';
  };
  for (const ch of s) {
    const j = keyToJamo(ch);
    if (!j) { flush(); out += ch; continue; }
    if (isVowel(j)) {
      if (jong) {
        // 받침이 다음 글자 첫소리로 넘어간다 (겹받침이면 뒤엣것만)
        const split = JONG_SPLIT[jong];
        const next = split ? split[1] : jong;
        jong = split ? split[0] : '';
        flush();
        cho = next; jung = j;
      } else if (jung) {
        const pair = VOWEL_PAIR[jung + j];
        if (pair) jung = pair;
        else { flush(); jung = j; }
      } else {
        jung = j;
      }
    } else {
      if (!jung) {
        flush(); cho = j;                         // 자음만 연달아
      } else if (!cho) {
        flush(); cho = j;                         // 모음만 있던 글자 뒤
      } else if (!jong) {
        if (JONG.includes(j)) jong = j;           // ㄸ·ㅃ·ㅉ은 받침이 안 됨
        else { flush(); cho = j; }
      } else {
        const pair = JONG_PAIR[jong + j];
        if (pair) jong = pair;
        else { flush(); cho = j; }
      }
    }
  }
  flush();
  return out;
}

const SYLLABLE = /[가-힣]/;
const LONE_JAMO = /[ㄱ-ㅎㅏ-ㅣ]/;

/**
 * 영어 상태로 친 한글로 보이면 바꾼 한글을, 아니면 null.
 * 영문자가 하나라도 있고, 바꾸면 완성된 한글이 나오고, 떨어진 자모가 없을 때만 (MSG → ㅡㄴㅎ 같은 건 제외)
 */
export function mistypedKorean(input) {
  const s = String(input == null ? '' : input);
  if (!/[a-zA-Z]/.test(s)) return null;
  const ko = engToKor(s);
  if (ko === s || !SYLLABLE.test(ko) || LONE_JAMO.test(ko)) return null;
  return ko;
}

/** 검색어 후보: 입력 그대로 + (영어로 친 한글이면) 바꾼 한글. 끝에 덜 친 자모가 붙어 있어도 검색엔 쓴다 */
export function searchVariants(input) {
  const s = String(input == null ? '' : input);
  const out = [s];
  if (/[a-zA-Z]/.test(s)) {
    const add = (src) => {
      const ko = engToKor(src);
      if (ko === src || !SYLLABLE.test(ko)) return;
      if (!out.includes(ko)) out.push(ko);
      const trimmed = ko.replace(/[ㄱ-ㅎㅏ-ㅣ]+$/, '');   // "간자ㅇ" → "간자" (치는 중)
      if (trimmed && !out.includes(trimmed)) out.push(trimmed);
      // 치는 중 겹받침: "갅"(rksw) → "간" — 다음 글자를 치면 "간자"가 된다
      const last = trimmed.charCodeAt(trimmed.length - 1) - 0xAC00;
      if (last >= 0 && last < 11172) {
        const jong = JONG[last % 28];
        const split = JONG_SPLIT[jong];
        if (split) {
          const reduced = trimmed.slice(0, -1) + String.fromCharCode(0xAC00 + (last - (last % 28)) + JONG.indexOf(split[0]));
          if (!out.includes(reduced)) out.push(reduced);
        }
      }
    };
    add(s);
    // Caps Lock이 켜진 채로 친 것(RKSWKD)은 소문자로도 한 번 더
    if (/[A-Z]/.test(s) && !/[a-z]/.test(s)) add(s.toLowerCase());
  }
  return out;
}
